import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, Phone, Heart, ArrowRight, CheckCircle, ShieldAlert } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import SafetyBanner from "../components/SafetyBanner";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    overline: "Get Support",
    title: "You Reached Out. That's the First Step.",
    subtitle: "Tell us only what feels safe to share. A member of The Pillar Project team will reach out in the way you tell us is safest. All contact is confidential.",
    channels: { call: "Call us", email: "Email us", hotline: "24/7 DV Hotline" },
    submittedTitle: "Your message reached us.",
    submittedText: "A member of The Pillar Project will follow up in the way you told us is safest. If you marked your request as urgent, we'll prioritize it.",
    submittedFallback: "If your situation changes and you need help immediately, call the National DV Hotline at 1-800-799-7233, or 911 if you are in danger.",
    intro: "Share only what feels safe. You don't need to describe your situation in detail here. We'll ask thoughtful questions later, at your pace.",
    fields: {
      name: "Preferred name", namePh: "What we should call you",
      county: "County or city", countyPh: "e.g. Baltimore City / Baltimore County",
      email: "Safe email", emailPh: "Only if safe to use",
      phone: "Safe phone number", phonePh: "Only if safe to use",
      method: "Preferred contact method", timeLabel: "Safe time to reach you", timePh: "e.g. weekdays 10am-2pm",
      language: "Language preference", urgency: "How urgent is this?",
      voicemail: "Is it safe to leave a voicemail?", text: "Is it safe to send a text?",
      support: "Type of support (select all that apply)",
      description: "Short description of the need", descriptionHint: "(optional — no need to describe abuse in detail here)",
      descriptionPh: "Anything you'd like us to know to prepare — as brief as you'd like.",
      consent: "I consent to be contacted by The Pillar Project using the contact methods I provided above. I understand this is not a guarantee of services.",
      submit: "Send Message", sending: "Sending…",
      yes: "Yes", no: "No", select: "Select…",
    },
    disclaimerTitle: "Please read before submitting:",
    disclaimer1: "Submitting this form does not guarantee shelter placement, financial assistance, legal representation, transportation, or acceptance into another provider's program. A member of The Pillar Project will review the request and provide available support or referral information based on your circumstances.",
    disclaimer2: "Please do not share Social Security numbers, medical records, immigration documents, or detailed abuse narratives through this form. We'll ask thoughtful, private questions later.",
    contactMethods: ["Safe email", "Safe phone call", "Text message", "Either"],
    languages: ["English", "Spanish", "Other"],
    urgencies: [
      { value: "urgent", label: "I need help right now (still safe to submit a form)" },
      { value: "soon", label: "Not urgent, but soon" },
      { value: "planning", label: "I'm gathering information / planning" },
    ],
    supportCategories: ["Crisis advocacy","Safety planning","Shelter or housing referral","Legal-resource referral","Mental-health referral","Transportation support","Job readiness","Training or certification","Clothing or essential needs","Other"],
    footerLocation: "Baltimore, Maryland",
    footerLocationSub: "Serving Baltimore and surrounding Maryland communities.",
    footerDanger: "In immediate danger? Please call 911.",
    footer247: "For 24/7 DV support, call the National Hotline.",
    toastConsent: "Please confirm consent to be contacted.",
    toastContact: "Please provide either a safe email or a safe phone number.",
    toastFail: "Submission failed. Please try again or call the DV hotline: 1-800-799-7233.",
    subjectPrefix: "Get Support",
  },
  es: {
    overline: "Obtener Ayuda",
    title: "Nos Contactaste. Ese Es el Primer Paso.",
    subtitle: "Comparte solo lo que sientas seguro compartir. Un miembro del equipo de The Pillar Project se comunicará contigo de la manera que nos digas que es más segura. Todo contacto es confidencial.",
    channels: { call: "Llámanos", email: "Escríbenos", hotline: "Línea 24/7 de VD" },
    submittedTitle: "Tu mensaje nos llegó.",
    submittedText: "Un miembro de The Pillar Project dará seguimiento de la manera que nos indicaste que es más segura. Si marcaste tu solicitud como urgente, la priorizaremos.",
    submittedFallback: "Si tu situación cambia y necesitas ayuda de inmediato, llama a la Línea Nacional contra la VD al 1-800-799-7233, o al 911 si estás en peligro.",
    intro: "Comparte solo lo que sientas seguro. No necesitas describir tu situación en detalle aquí. Haremos preguntas cuidadosas después, a tu ritmo.",
    fields: {
      name: "Nombre preferido", namePh: "Cómo debemos llamarte",
      county: "Condado o ciudad", countyPh: "p.ej. Ciudad de Baltimore / Condado de Baltimore",
      email: "Correo seguro", emailPh: "Solo si es seguro usarlo",
      phone: "Teléfono seguro", phonePh: "Solo si es seguro usarlo",
      method: "Método de contacto preferido", timeLabel: "Horario seguro para contactarte", timePh: "p.ej. entre semana 10am-2pm",
      language: "Preferencia de idioma", urgency: "¿Qué tan urgente es?",
      voicemail: "¿Es seguro dejar un mensaje de voz?", text: "¿Es seguro enviar un mensaje de texto?",
      support: "Tipo de apoyo (selecciona todos los que apliquen)",
      description: "Breve descripción de la necesidad", descriptionHint: "(opcional — no necesitas describir el abuso en detalle aquí)",
      descriptionPh: "Cualquier cosa que quieras que sepamos para prepararnos — tan breve como quieras.",
      consent: "Consiento ser contactado(a) por The Pillar Project usando los métodos de contacto que proporcioné arriba. Entiendo que esto no es una garantía de servicios.",
      submit: "Enviar Mensaje", sending: "Enviando…",
      yes: "Sí", no: "No", select: "Seleccionar…",
    },
    disclaimerTitle: "Por favor lee antes de enviar:",
    disclaimer1: "Enviar este formulario no garantiza colocación en refugio, asistencia financiera, representación legal, transporte, ni aceptación en el programa de otro proveedor. Un miembro de The Pillar Project revisará la solicitud y proporcionará el apoyo o la información de referencia disponible según tus circunstancias.",
    disclaimer2: "Por favor no compartas números de Seguro Social, expedientes médicos, documentos de inmigración ni relatos detallados de abuso a través de este formulario. Haremos preguntas cuidadosas y privadas después.",
    contactMethods: ["Correo seguro", "Llamada telefónica segura", "Mensaje de texto", "Cualquiera"],
    languages: ["Inglés", "Español", "Otro"],
    urgencies: [
      { value: "urgent", label: "Necesito ayuda ahora mismo (aún es seguro enviar un formulario)" },
      { value: "soon", label: "No urgente, pero pronto" },
      { value: "planning", label: "Estoy reuniendo información / planificando" },
    ],
    supportCategories: ["Defensa en crisis","Planificación de seguridad","Referencia de refugio o vivienda","Referencia de recursos legales","Referencia de salud mental","Apoyo de transporte","Preparación laboral","Capacitación o certificación","Ropa o necesidades esenciales","Otro"],
    footerLocation: "Baltimore, Maryland",
    footerLocationSub: "Sirviendo a Baltimore y las comunidades circundantes de Maryland.",
    footerDanger: "¿En peligro inmediato? Por favor llama al 911.",
    footer247: "Para apoyo de VD 24/7, llama a la Línea Nacional.",
    toastConsent: "Por favor confirma el consentimiento para ser contactado.",
    toastContact: "Por favor proporciona un correo seguro o un teléfono seguro.",
    toastFail: "El envío falló. Intenta de nuevo o llama a la línea de VD: 1-800-799-7233.",
    subjectPrefix: "Obtener Ayuda",
  },
};

const ContactPage = () => {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;

  const [form, setForm] = useState({
    preferred_name: "", safe_email: "", safe_phone: "",
    preferred_contact_method: "", safe_time: "", county: "",
    support_categories: [], language: c.languages[0], voicemail_ok: "", text_ok: "",
    emergency_status: "", short_description: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleCategory = (cat) => {
    setForm((f) => ({ ...f, support_categories: f.support_categories.includes(cat) ? f.support_categories.filter((x) => x !== cat) : [...f.support_categories, cat] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error(c.toastConsent);
    if (!form.safe_email && !form.safe_phone) return toast.error(c.toastContact);
    setSubmitting(true);
    try {
      const messageBlock = [
        `Preferred name: ${form.preferred_name || "(not provided)"}`,
        `County/city: ${form.county || "(not provided)"}`,
        `Preferred contact method: ${form.preferred_contact_method || "(not provided)"}`,
        `Safe time to contact: ${form.safe_time || "(any)"}`,
        `Language: ${form.language}`,
        `Safe to leave voicemail: ${form.voicemail_ok || "(not answered)"}`,
        `Safe to send text: ${form.text_ok || "(not answered)"}`,
        `Emergency status: ${form.emergency_status || "(not answered)"}`,
        `Support requested: ${form.support_categories.join(", ") || "(none selected)"}`,
        "",
        `Short description:`,
        form.short_description || "(none)",
      ].join("\n");

      await axios.post(`${API}/contacts`, {
        name: form.preferred_name || "Anonymous",
        email: form.safe_email || "no-email@thepillarprojectbaltimore.org",
        phone: form.safe_phone || "",
        subject: `${c.subjectPrefix}: ${form.support_categories[0] || "General"}${form.emergency_status === "urgent" ? " (URGENT)" : ""}`,
        message: messageBlock,
        inquiry_type: "need-help",
      });
      setSubmitted(true);
    } catch {
      toast.error(c.toastFail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SafetyBanner />

      <section className="bg-warm-800 text-white py-14 md:py-20" data-testid="get-support-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-300 mb-4 block">{c.overline}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }} data-testid="contact-hero-title">{c.title}</h1>
            <p className="text-warm-200 text-lg leading-relaxed max-w-2xl">{c.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-8 border-b border-gray-100" data-testid="quick-channels">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-3 gap-6 text-sm">
          <ChannelCard icon={Phone} title={c.channels.call} body="(347) 221-4189" href="tel:347-221-4189" testId="qc-call" />
          <ChannelCard icon={Mail} title={c.channels.email} body="director@thepillarprojectbaltimore.org" href="mailto:director@thepillarprojectbaltimore.org" testId="qc-email" />
          <ChannelCard icon={ShieldAlert} title={c.channels.hotline} body="1-800-799-7233" href="tel:1-800-799-7233" testId="qc-hotline" red />
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          {submitted ? (
            <div className="bg-warm-50 border border-warm-100 rounded-2xl p-10 text-center" data-testid="contact-submitted">
              <CheckCircle className="w-14 h-14 text-warm-700 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-gray-900 mb-3" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.submittedTitle}</h3>
              <p className="text-gray-700 mb-4">{c.submittedText}</p>
              <p className="text-sm text-gray-500">{c.submittedFallback}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="get-support-form">
              <div className="bg-warm-50 border border-warm-100 rounded-xl p-4 text-sm text-gray-700 flex items-start gap-3">
                <Heart className="w-5 h-5 text-warm-700 flex-shrink-0 mt-0.5" />
                <p>{c.intro}</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <F label={c.fields.name} placeholder={c.fields.namePh} value={form.preferred_name} onChange={(v) => setForm({ ...form, preferred_name: v })} testId="gs-name" />
                <F label={c.fields.county} placeholder={c.fields.countyPh} value={form.county} onChange={(v) => setForm({ ...form, county: v })} testId="gs-county" />
                <F label={c.fields.email} type="email" placeholder={c.fields.emailPh} value={form.safe_email} onChange={(v) => setForm({ ...form, safe_email: v })} testId="gs-email" />
                <F label={c.fields.phone} type="tel" placeholder={c.fields.phonePh} value={form.safe_phone} onChange={(v) => setForm({ ...form, safe_phone: v })} testId="gs-phone" />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <S label={c.fields.method} options={c.contactMethods} value={form.preferred_contact_method} onChange={(v) => setForm({ ...form, preferred_contact_method: v })} selectLabel={c.fields.select} testId="gs-method" />
                <F label={c.fields.timeLabel} placeholder={c.fields.timePh} value={form.safe_time} onChange={(v) => setForm({ ...form, safe_time: v })} testId="gs-time" />
                <S label={c.fields.language} options={c.languages} value={form.language} onChange={(v) => setForm({ ...form, language: v })} selectLabel={c.fields.select} testId="gs-language" />
                <S label={c.fields.urgency} options={c.urgencies} objectOptions value={form.emergency_status} onChange={(v) => setForm({ ...form, emergency_status: v })} selectLabel={c.fields.select} testId="gs-urgency" />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <R label={c.fields.voicemail} yes={c.fields.yes} no={c.fields.no} value={form.voicemail_ok} onChange={(v) => setForm({ ...form, voicemail_ok: v })} testId="gs-voicemail" />
                <R label={c.fields.text} yes={c.fields.yes} no={c.fields.no} value={form.text_ok} onChange={(v) => setForm({ ...form, text_ok: v })} testId="gs-text" />
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">{c.fields.support}</Label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {c.supportCategories.map((cat) => (
                    <label key={cat} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" checked={form.support_categories.includes(cat)} onChange={() => toggleCategory(cat)} className="mt-1 accent-warm-700" data-testid={`gs-cat-${cat.substring(0, 8).replace(/\s+/g, "-").toLowerCase()}`} />
                      <span>{cat}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  {c.fields.description} <span className="text-xs text-gray-500 font-normal">{c.fields.descriptionHint}</span>
                </Label>
                <Textarea value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="rounded-lg min-h-[100px]" placeholder={c.fields.descriptionPh} data-testid="gs-description" />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-700 leading-relaxed">
                <p className="font-semibold text-gray-900 mb-1">{c.disclaimerTitle}</p>
                <p>{c.disclaimer1}</p>
                <p className="mt-2">{c.disclaimer2}</p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="gs-consent-wrap">
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-warm-700" data-testid="gs-consent" />
                <span className="text-sm text-gray-700">{c.fields.consent}</span>
              </label>

              <Button type="submit" disabled={submitting} className="bg-warm-700 hover:bg-warm-800 text-white rounded-full px-8 py-5" data-testid="get-support-submit">
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {c.fields.sending}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" /> {c.fields.submit} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      <section className="py-14 bg-warm-800 text-white">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.footerLocation}</h2>
            <p className="text-warm-200 text-sm flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {c.footerLocationSub}
            </p>
          </div>
          <div>
            <p className="text-sm text-warm-200 mb-2">{c.footerDanger}</p>
            <p className="text-sm text-warm-200 mb-4">{c.footer247}</p>
            <a href="tel:1-800-799-7233">
              <Button className="bg-white text-warm-800 hover:bg-warm-100 rounded-full px-6 py-5 font-semibold" data-testid="cta-dv-hotline">1-800-799-7233</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

function F({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <Label className="text-sm font-medium text-gray-700 mb-1.5 block">{label}</Label>
      <Input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="rounded-lg" data-testid={testId} />
    </div>
  );
}

function S({ label, options, value, onChange, objectOptions = false, selectLabel, testId }) {
  return (
    <div>
      <Label className="text-sm font-medium text-gray-700 mb-1.5 block">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white" data-testid={testId}>
        <option value="">{selectLabel}</option>
        {options.map((o) => {
          const val = objectOptions ? o.value : o;
          const lbl = objectOptions ? o.label : o;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    </div>
  );
}

function R({ label, yes, no, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-sm font-medium text-gray-700 mb-2 block">{label}</Label>
      <div className="flex gap-2" data-testid={testId}>
        {[yes, no].map((opt) => (
          <button
            type="button"
            key={opt}
            onClick={() => onChange(opt)}
            className={`px-4 py-2 rounded-full border text-sm ${value === opt ? "bg-warm-700 text-white border-warm-700" : "bg-white border-gray-200 text-gray-700 hover:border-warm-300"}`}
            data-testid={`${testId}-${opt.toLowerCase()}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChannelCard({ icon: Icon, title, body, href, red, testId }) {
  return (
    <a href={href} className={`block border rounded-xl p-5 hover:shadow-sm transition-shadow ${red ? "border-red-200 bg-red-50" : "border-gray-100 bg-white"}`} data-testid={testId}>
      <Icon className={`w-6 h-6 mb-2 ${red ? "text-red-700" : "text-warm-700"}`} />
      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{title}</p>
      <p className="font-semibold text-gray-900">{body}</p>
    </a>
  );
}

export default ContactPage;
